import type { PxlScores } from './pxl-scores';
import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from 'helpers/cn';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';

const SECONDARY_BUTTON_CLASS =
  'border-line text-foreground hover:bg-hover h-8 rounded-lg border bg-transparent px-3 text-meta';

const PRIMARY_BUTTON_CLASS = 'bg-cta hover:bg-ctahover h-8 rounded-lg px-3 text-meta text-white';

type PXLRatingFlowProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete: (scores: PxlScores) => void;
  initialScores?: PxlScores | null;
};

type Step = 'p' | 'x' | 'l' | 'summary';

const STEP_CONFIG = {
  p: {
    title: 'Potential (P)',
    question: 'What is the potential impact of this experiment?',
    guidance: [
      { value: '1', label: 'Very low potential impact' },
      { value: '2', label: 'Low potential impact' },
      { value: '3', label: 'Moderate potential impact' },
      { value: '4', label: 'High potential impact' },
      { value: '5', label: 'Very high potential impact' },
    ],
  },
  x: {
    title: 'Exposure (X)',
    question: 'How many users will be exposed to this change?',
    guidance: [
      { value: '1', label: 'Very few users (< 5%)' },
      { value: '2', label: 'Few users (5-15%)' },
      { value: '3', label: 'Moderate exposure (15-50%)' },
      { value: '4', label: 'Many users (50-85%)' },
      { value: '5', label: 'Almost all users (> 85%)' },
    ],
  },
  l: {
    title: 'Level of Effort (L)',
    question: 'What is the level of effort required?',
    guidance: [
      { value: '1', label: 'Very high effort (weeks/months)' },
      { value: '2', label: 'High effort (days/weeks)' },
      { value: '3', label: 'Moderate effort (days)' },
      { value: '4', label: 'Low effort (hours/days)' },
      { value: '5', label: 'Very low effort (hours)' },
    ],
  },
} as const;

const STEPS: readonly ('p' | 'x' | 'l')[] = ['p', 'x', 'l'];

export const PXLRatingFlow = ({ open, onOpenChange, onComplete, initialScores }: PXLRatingFlowProps) => {
  const [currentStep, setCurrentStep] = useState<Step>('p');
  const [scores, setScores] = useState({ p: '', x: '', l: '' });

  useEffect(() => {
    if (open) {
      setCurrentStep('p');
      setScores({
        p: initialScores?.p ? String(initialScores.p) : '',
        x: initialScores?.x ? String(initialScores.x) : '',
        l: initialScores?.l ? String(initialScores.l) : '',
      });
    }
  }, [open]);

  const stepIndex = currentStep === 'summary' ? 3 : STEPS.indexOf(currentStep as 'p' | 'x' | 'l');
  const progress = currentStep === 'summary' ? 100 : Math.round(((stepIndex + 1) / 3) * 100);
  const stepNumber = Math.min(stepIndex + 1, 3);

  const handleNext = () => {
    if (currentStep === 'summary') return;
    const idx = STEPS.indexOf(currentStep as 'p' | 'x' | 'l');
    if (!scores[STEPS[idx]!]) return;
    if (idx < 2) {
      setCurrentStep(STEPS[idx + 1]!);
    } else {
      setCurrentStep('summary');
    }
  };

  const handleBack = () => {
    if (currentStep === 'summary') {
      setCurrentStep('l');
    } else {
      const idx = STEPS.indexOf(currentStep as 'p' | 'x' | 'l');
      if (idx > 0) setCurrentStep(STEPS[idx - 1]!);
    }
  };

  const handleComplete = () => {
    onComplete({
      p: parseInt(scores.p),
      x: parseInt(scores.x),
      l: parseInt(scores.l),
    });
    onOpenChange(false);
  };

  const handleScoreChange = (step: 'p' | 'x' | 'l', value: string) => {
    setScores((prev) => ({ ...prev, [step]: value }));
  };

  const calculateFinalScore = () => {
    const p = parseInt(scores.p) || 0;
    const x = parseInt(scores.x) || 0;
    const l = parseInt(scores.l) || 0;
    return ((p + x + l) / 3).toFixed(1);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-line bg-panel sm:rounded-panel max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-title font-medium">Importance — PXL Framework</DialogTitle>
          <DialogDescription className="supporting">
            Rate your experiment across three dimensions to calculate its priority score.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-5 py-2">
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <span className="micro-label">Step {stepNumber} of 3</span>
              <span className="num text-dim">{progress}%</span>
            </div>
            <div className="bg-track h-[3px] w-full overflow-hidden rounded-pill">
              <div className="bg-accent h-full rounded-pill transition-all" style={{ width: `${progress}%` }} />
            </div>
          </div>

          {currentStep === 'summary' ? (
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-1">
                <h3 className="micro-label text-foreground">PXL rating summary</h3>
                <p className="supporting">Review your ratings before completing.</p>
              </div>

              <div className="flex flex-col gap-2">
                {STEPS.map((step) => (
                  <div
                    key={step}
                    className="border-line bg-panel2 rounded-card flex items-center justify-between gap-3 border p-3"
                  >
                    <div className="min-w-0">
                      <div className="text-foreground truncate text-body font-medium">{STEP_CONFIG[step].title}</div>
                      <div className="supporting truncate">
                        {STEP_CONFIG[step].guidance.find((g) => g.value === scores[step])?.label}
                      </div>
                    </div>
                    <div className="num text-foreground shrink-0">{scores[step]}</div>
                  </div>
                ))}
              </div>

              <div className="border-line bg-panel2 rounded-card border p-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="micro-label text-foreground">Final score</div>
                    <div className="supporting">Average of P, X, L</div>
                  </div>
                  <div className="metric-value text-foreground shrink-0">{calculateFinalScore()} / 5.0</div>
                </div>
              </div>

              <div className="flex gap-2">
                <Button variant="outline" onClick={handleBack} className={cn(SECONDARY_BUTTON_CLASS, 'flex-1')}>
                  <ChevronLeft className="size-3.5" strokeWidth={1.5} />
                  Back
                </Button>
                <Button onClick={handleComplete} className={cn(PRIMARY_BUTTON_CLASS, 'flex-1')}>
                  Complete
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-1">
                <h3 className="micro-label text-foreground">{STEP_CONFIG[currentStep].title}</h3>
                <p className="supporting">{STEP_CONFIG[currentStep].question}</p>
              </div>

              <RadioGroup
                value={scores[currentStep]}
                onValueChange={(value) => handleScoreChange(currentStep, value)}
                className="flex flex-col gap-2"
              >
                {STEP_CONFIG[currentStep].guidance.map((item) => (
                  <div
                    key={item.value}
                    className={cn(
                      'rounded-card flex cursor-pointer items-center gap-3 border p-3 transition-colors duration-150',
                      scores[currentStep] === item.value
                        ? 'border-sel-line bg-sel-bg'
                        : 'border-line bg-panel2 hover:bg-hover',
                    )}
                    onClick={() => handleScoreChange(currentStep, item.value)}
                  >
                    <RadioGroupItem value={item.value} id={`${currentStep}-${item.value}`} />
                    <Label
                      htmlFor={`${currentStep}-${item.value}`}
                      className="flex-1 cursor-pointer text-meta font-normal"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-foreground truncate">{item.label}</span>
                        <span className="num text-dim shrink-0">{item.value}</span>
                      </div>
                    </Label>
                  </div>
                ))}
              </RadioGroup>

              <div className="flex gap-2">
                {currentStep !== 'p' && (
                  <Button variant="outline" onClick={handleBack} className={cn(SECONDARY_BUTTON_CLASS, 'flex-1')}>
                    <ChevronLeft className="size-3.5" strokeWidth={1.5} />
                    Back
                  </Button>
                )}
                <Button
                  onClick={handleNext}
                  disabled={!scores[currentStep]}
                  className={cn(PRIMARY_BUTTON_CLASS, currentStep === 'p' ? 'w-full' : 'flex-1')}
                >
                  Next
                  <ChevronRight className="size-3.5" strokeWidth={1.5} />
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
